import ONBOARDING_KEYS from "./keys";
import { API_DOMAIN } from "config";

const onboardingSyncStatusByUser = new Map();
const ONBOARDING_TOUR_NAMES = ["welcome", "catalog", "vehicle", "offers"];

function getSyncUserKey(userId) {
  return String(userId ?? "guest");
}

function setSyncStatus(userId, status) {
  onboardingSyncStatusByUser.set(getSyncUserKey(userId), status);
}

export function isOnboardingStateSyncReady(userId) {
  if (!userId) return true;
  const status = onboardingSyncStatusByUser.get(getSyncUserKey(userId));
  return status === "success" || status === "failed";
}

/**
 * getOnboardingKey — מחזיר key ייחודי לפי משתמש
 * @param {string} baseKey - מפתח בסיס מ-ONBOARDING_KEYS
 * @param {string|number} [userId] - ID משתמש (אופציונלי)
 * @returns {string} - key מלא עם userId אם קיים
 */
export function getOnboardingKey(baseKey, userId) {
  if (!userId) return baseKey;
  return `${baseKey}_${userId}`;
}

/**
 * hasSeenWelcome — בודק אם המשתמש כבר ראה את ה-Welcome Modal
 */
export function hasSeenWelcome(userId) {
  try {
    const key = getOnboardingKey(ONBOARDING_KEYS.welcomeSeen, userId);
    return localStorage.getItem(key) === "true";
  } catch {
    return true; // אם יש שגיאה, לא להציג
  }
}

/**
 * markWelcomeSeen — מסמן שהמשתמש ראה את ה-Welcome Modal
 */
export async function markWelcomeSeen(userId) {
  try {
    const key = getOnboardingKey(ONBOARDING_KEYS.welcomeSeen, userId);
    localStorage.setItem(key, "true");

    if (userId && API_DOMAIN) {
      await fetch(`${API_DOMAIN}/user/setOnboardingState`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          tour_name: "welcome",
          done: 1,
        }),
      });
    }
  } catch (err) {
    console.warn("Failed to save welcome seen:", err);
  }
}

function setAllOnboardingKeys(userId, value) {
  const stringValue = value ? "true" : "false";
  Object.values(ONBOARDING_KEYS).forEach((baseKey) => {
    const key = getOnboardingKey(baseKey, userId);
    localStorage.setItem(key, stringValue);
  });
}

async function postOnboardingState(body) {
  const res = await fetch(`${API_DOMAIN}/user/setOnboardingState`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });

  const data = await res.json().catch(() => null);
  if (!res.ok || data?.success === false) {
    throw new Error("Failed to persist onboarding state");
  }

  return data;
}

async function persistAllOnboardingDone(userId) {
  if (!userId || !API_DOMAIN) return;

  try {
    await postOnboardingState({
      tour_name: "all",
      done: 1,
      welcome_seen: 1,
      catalog_done: 1,
      vehicle_done: 1,
      offers_done: 1,
      onboarding_skipped: 1,
    });
  } catch {}

  await Promise.all(
    ONBOARDING_TOUR_NAMES.map((tourName) =>
      postOnboardingState({
        tour_name: tourName,
        done: 1,
      })
    )
  );
}

export async function markAllOnboardingSkipped(userId) {
  try {
    setAllOnboardingKeys(userId, true);
    await persistAllOnboardingDone(userId);
  } catch (err) {
    console.warn("Failed to mark onboarding skipped:", err);
  }
}

/**
 * isTourDone — בודק אם Tour מסוים הושלם
 * @param {string} tourName - "catalog" | "vehicle" | "offers"
 */
export function isTourDone(tourName, userId) {
  try {
    const keyMap = {
      catalog: ONBOARDING_KEYS.catalogTourDone,
      vehicle: ONBOARDING_KEYS.vehicleTourDone,
      offers: ONBOARDING_KEYS.offersTourDone,
    };
    const baseKey = keyMap[tourName];
    if (!baseKey) return true;
    const key = getOnboardingKey(baseKey, userId);

    if (userId) {
      return localStorage.getItem(key) === "true";
    }

    return localStorage.getItem(baseKey) === "true";
  } catch {
    return true;
  }
}

/**
 * markTourDone — מסמן Tour כהושלם
 * @param {string} tourName - "catalog" | "vehicle" | "offers"
 */
export function markTourDone(tourName, userId) {
  try {
    const keyMap = {
      catalog: ONBOARDING_KEYS.catalogTourDone,
      vehicle: ONBOARDING_KEYS.vehicleTourDone,
      offers: ONBOARDING_KEYS.offersTourDone,
    };
    const baseKey = keyMap[tourName];
    if (!baseKey) return;
    const key = getOnboardingKey(baseKey, userId);

    localStorage.setItem(key, "true");

    if (userId && API_DOMAIN) {
      fetch(`${API_DOMAIN}/user/setOnboardingState`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          tour_name: tourName,
          done: 1,
        }),
      }).catch(() => {});
    }
  } catch (err) {
    console.warn(`Failed to save ${tourName} tour done:`, err);
  }
}

/**
 * syncOnboardingStateFromServer — טוען מצב סיורים מהשרת ומסנכרן ל-localStorage
 * כדי לאפשר "פעם אחת לכל משתמש" גם בין דפדפנים שונים.
 */
export async function syncOnboardingStateFromServer(userId) {
  const syncKey = getSyncUserKey(userId);
  try {
    if (!userId || !API_DOMAIN) {
      setSyncStatus(syncKey, "failed");
      return { status: "failed", state: null };
    }

    setSyncStatus(syncKey, "pending");

    const res = await fetch(`${API_DOMAIN}/user/getOnboardingState`, {
      credentials: "include",
    });
    if (!res.ok) {
      setSyncStatus(syncKey, "failed");
      return { status: "failed", state: null };
    }

    const data = await res.json();
    if (!data?.success || !data?.state) {
      setSyncStatus(syncKey, "failed");
      return { status: "failed", state: null };
    }

    const state = data.state;
    const writeBool = (baseKey, value) => {
      const scopedKey = getOnboardingKey(baseKey, userId);
      localStorage.setItem(scopedKey, value ? "true" : "false");
    };

    writeBool(ONBOARDING_KEYS.welcomeSeen, Number(state.welcome_seen) === 1);
    writeBool(ONBOARDING_KEYS.catalogTourDone, Number(state.catalog_done) === 1);
    writeBool(ONBOARDING_KEYS.vehicleTourDone, Number(state.vehicle_done) === 1);
    writeBool(ONBOARDING_KEYS.offersTourDone, Number(state.offers_done) === 1);
    setSyncStatus(syncKey, "success");
    return {
      status: "success",
      state,
      welcomeSeen: Number(state.welcome_seen) === 1,
      catalogDone: Number(state.catalog_done) === 1,
      vehicleDone: Number(state.vehicle_done) === 1,
      offersDone: Number(state.offers_done) === 1,
    };
  } catch {
    setSyncStatus(syncKey, "failed");
    // Silent fallback to localStorage-only mode.
    return { status: "failed", state: null };
  }
}

/**
 * resetAllToursForUser — מאפס את כל ה-Tours עבור משתמש ספציפי
 * (לשימוש בכפתור "הפעל שוב את הסיור המודרך")
 */
export function resetAllToursForUser(userId) {
  try {
    Object.values(ONBOARDING_KEYS).forEach((baseKey) => {
      const key = getOnboardingKey(baseKey, userId);
      localStorage.removeItem(key);
    });
  } catch (err) {
    console.warn("Failed to reset tours:", err);
  }
}
